import React, { useCallback, useState } from 'react';
import PeriodRenderComponent, {
  checkMoreThanOneYear,
  checkIsContinuously,
} from './PeriodRenderComponent';
import { SectionBase } from '../SectionBase';
import {
  isCharityNavigatorDateValid,
  ParentPeriodValue,
  parsePeriodValues,
} from './utils';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import { BottomBar } from '../BottomBar';
import TrueImpactModal from 'shared/design-system/components/TrueImpactModal';
import WarningModal from './WarningModal';
import { GuidanceTextResponse } from '../utils';
import { Report } from 'shared/components/WelcomeBack/utils';
import { useReport } from 'shared/providers/ReportProvider';
import { useUserActions } from 'shared/auth/hooks/useUserActions';

const isLessThanAYear = (
  startDate: string | Date,
  endDate: string | Date,
): boolean => {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;

  // Convert the difference to days (1 day = 24*60*60*1000 milliseconds)
  const difference = end.getTime() - start.getTime();

  const differenceInDays = difference / (24 * 60 * 60 * 1000);

  return differenceInDays < 365;
};

interface SectionDatesProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any;
  report: Report;
  isLoading: boolean;
  helpText: string;
  reportId: number;
  programId: number;
  reportYear?: string;
  guidanceText: GuidanceTextResponse;
  handleBack: () => unknown;
  handleNext: (pressedButton?: boolean, forcedValues?: string) => unknown;
  onChange: (questionId: number, value: ParentPeriodValue | null) => unknown;
  options: string[];
  otherType: 'period';
  questionId: number;
  subTitle: JSX.Element | JSX.Element[];
  title: string;
  value: ParentPeriodValue | null;
  values: { [questionId: number]: string };
}

interface DateModal {
  showWarning: false | 'less' | 'more';
  onModalClose: () => unknown;
  handleNext: (pressedButton?: boolean, forcedValues?: string) => unknown;
}

export const DateModal: React.FC<DateModal> = ({
  showWarning,
  handleNext,
  onModalClose,
}) => {
  return (
    <TrueImpactModal
      onClose={onModalClose}
      open={Boolean(showWarning)}
      hideHeader={true}
      maxHeight="466px"
      maxWidth="741px"
      next={{
        onClick: handleNext,
        text: 'Use my selected dates',
      }}
      previous={{
        onClick: onModalClose,
        text: 'Change my dates',
      }}
    >
      <WarningModal showWarning={showWarning} />
    </TrueImpactModal>
  );
};

const heading = 'What are the scheduled dates for your long-term program?';
const subTitleForMoreThanYear =
  'This is appropriate for programs that take more than a year to complete the delivery of their service. For example, a three-year ' +
    'apprentice program might start September 1, 2023 and end June 30, 2026, which is typically when the end beneficiaries achieve ' +
    'success. If this sounds like your program, enter the specific dates below. If not, select an annual reporting period instead.';

const SectionPeriod: React.FC<SectionDatesProps> = ({
  error,
  report,
  helpText,
  guidanceText,
  handleBack,
  handleNext,
  onChange,
  reportYear,
  options,
  questionId,
  subTitle,
  isLoading,
  title,
  value,
  values,
  reportId,
}) => {
  const { isCNUser } = useUserActions();
  const { handleReportDate } = useReport();
  const [showWarning, setShowWarning] = useState<false | 'less' | 'more'>(
    false,
  );
  const isMoreThanOneYear =
    !checkIsContinuously(values[10002]) && checkMoreThanOneYear(values[10004]);

  const onModalClose = useCallback(() => {
    setShowWarning(false);
  }, [setShowWarning]);

  const onNext = useCallback(() => {
    if (!value) return;
    const period = parsePeriodValues(value, options);
    if (!period || !period.startDate || !period.endDate) return;
    handleReportDate(period.startDate, period.endDate);
    const isDatesLessThanYear = isLessThanAYear(
      period.startDate,
      period.endDate,
    );
    if (!values[10002].startsWith('Continuously')) {
      if (isMoreThanOneYear && isDatesLessThanYear) {
        setShowWarning('less');
        return;
      }
      if (!isMoreThanOneYear && !isDatesLessThanYear) {
        setShowWarning('more');
        return;
      }
    }
    handleNext(true);
    setShowWarning(false);
  }, [value, isMoreThanOneYear]);

  if (isLoading) {
    return <TrueImpactLoading />;
  }

  return (
    <>
      <SectionBase
        title={title}
        subTitle={isMoreThanOneYear ? heading : subTitle}
        helpText={isMoreThanOneYear ? subTitleForMoreThanYear : helpText}
        error={error}
        questionId={questionId}
        guidanceText={guidanceText}
      >
        <PeriodRenderComponent
          reportId={reportId}
          reportYear={reportYear}
          value={value}
          options={options}
          questionId={questionId}
          onChange={onChange}
          isMoreThanOneYear={isMoreThanOneYear}
          isThirdParty={isCNUser}
        />
      </SectionBase>
      <DateModal
        showWarning={showWarning}
        onModalClose={onModalClose}
        handleNext={handleNext}
      />
      <BottomBar
        disabled={false}
        hideNext={isCNUser ? !isCharityNavigatorDateValid(value) : !value}
        backTitle="Back"
        nextTitle="Save &amp; Continue"
        errorText={
          isCNUser && value && !isCharityNavigatorDateValid(value)
            ? 'Selected end date must be in the past'
            : undefined
        }
        handleNext={onNext}
        handleBack={handleBack}
      />
    </>
  );
};

export default SectionPeriod;
